import React, { useEffect, useState } from "react";
import { CircularProgress, Modal, Skeleton } from "@mui/material";
import { useDispatch } from "react-redux";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import TextInputField from "../common/TextInputField";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import InBodyTestCard from "./InBodyTestCard";
import StepProgressBar from "../common/StepProgressBar";
import {
  EditCustomerPersonalInfo,
  getInbodyData,
  getInbodyDateTimes,
  getPlatformData,
  InbodyDateTime,
  UpdateCostumerBiomarkers,
} from "../../../../common/network/Api";
import { platformActions } from "../../store/platform-slice";
import { useBiomarkersSavedSnackbar } from "../../hooks/useBiomarkersSavedSnackbar";

type Props = {
  open: boolean;
  onClose: () => void;
  // Opens the host screen's Review health scores modal, offered by the
  // post-import snackbar when the imported tests left scores pending. Same
  // contract as BiomarkerExtractorModal's: omitted where there is no such
  // modal, which falls back to the plain success toast.
  onReviewScores?: () => void;
};

const TOTAL_STEPS = 2;

const InBodySyncModal = ({ open, onClose, onReviewScores }: Props) => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const { AuthUser } = useAuth();
  const {
    ASSelectedClient,
    ASSetSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASGetClientBiomarkerTabDataHandler,
  } = useAppStore();
  const showSavedSnackbar = useBiomarkersSavedSnackbar(onReviewScores);

  const [step, setStep] = useState(1);
  const [inBodyAccount, setInBodyAccount] = useState("");
  const [tests, setTests] = useState<InbodyDateTime[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());
  // Per-test biomarker data, prefetched on load (see loadPreviews).
  const [previews, setPreviews] = useState<
    Record<string, { status: "loading" | "done" | "error"; data: any[] }>
  >({});
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const customerId = ASSelectedClient?.id ?? "";
  // The button is disabled (gray bg) while loading, so use a dark neutral for
  // the spinner instead of white.
  const spinnerColor = theme.colors_ds.NEUTRAL_TEXT_WEAK;

  // Reset to a clean state each time the modal opens, prefilling the client's
  // InBody code from the profile already loaded on selection.
  useEffect(() => {
    if (open) {
      setStep(1);
      setInBodyAccount(ASSelectedClient?.InBodyAccount ?? "");
      setTests([]);
      setSelectedIds(new Set());
      setExpandedIds(new Set());
      setErroredIds(new Set());
      setPreviews({});
      setIsPrefetching(false);
    }
    // Reset only when the modal opens — NOT when the client's InBody code
    // changes (we update it in the store after linking, and that must not
    // reset the wizard back to step 1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const notify = (title: string, message: string, type: string) => {
    ASSetSnackBarMessage({ title, message });
    ASSetTypeSnackBar(type);
    ASSetOpenSnackBar(true);
  };

  const importableTests = tests.filter(
    (t) => t.exists !== 1 && !erroredIds.has(t.id)
  );
  const allSelected =
    importableTests.length > 0 &&
    importableTests.every((t) => selectedIds.has(t.id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(importableTests.map((t) => t.id)));
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const anyExpanded = expandedIds.size > 0;

  // A test whose preview failed to load can't be imported — flag it and drop it
  // from the current selection.
  const markError = (id: string) => {
    setErroredIds((prev) => new Set(prev).add(id));
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Prefetch every test's biomarkers on load, in batches (capped concurrency),
  // so counts/EQUIP show immediately and Import can reuse the cache. Tests that
  // fail are flagged and dropped from the selection up front.
  const loadPreviews = async (list: InbodyDateTime[]) => {
    setPreviews(
      Object.fromEntries(
        list.map((t) => [t.id, { status: "loading" as const, data: [] }])
      )
    );
    setIsPrefetching(true);
    const CONCURRENCY = 4;
    try {
      for (let i = 0; i < list.length; i += CONCURRENCY) {
        const batch = list.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          batch.map(async (t) => {
            try {
              const data = await getInbodyData(customerId, t.id);
              const arr = Array.isArray(data) ? data : [];
              setPreviews((prev) => ({
                ...prev,
                [t.id]: { status: "done", data: arr },
              }));
            } catch (error) {
              console.error("Error loading InBody test:", error);
              setPreviews((prev) => ({
                ...prev,
                [t.id]: { status: "error", data: [] },
              }));
              markError(t.id);
            }
          })
        );
      }
    } finally {
      setIsPrefetching(false);
    }
  };

  const handleLinkAndContinue = async () => {
    if (!inBodyAccount.trim() || !customerId || isLinking) return;
    setIsLinking(true);
    try {
      // Persist the InBody code onto the customer profile (UpdateProfile), same
      // endpoint the Biometric tab uses to edit personal info. Skip the update
      // when the code is unchanged — resending the same value errors on the backend.
      const enteredCode = inBodyAccount.trim();
      const existingCode = (ASSelectedClient?.InBodyAccount ?? "").trim();
      if (enteredCode !== existingCode) {
        await EditCustomerPersonalInfo(customerId, {
          inBodyAccount: enteredCode,
        });
        // Keep the stored client in sync so a subsequent Link with the same
        // code is correctly skipped (avoids the backend 422 for no-op updates).
        if (ASSelectedClient) {
          ASSetSelectedClient({
            ...ASSelectedClient,
            InBodyAccount: enteredCode,
          });
        }
      }

      const data = await getInbodyDateTimes(customerId);
      const list: InbodyDateTime[] = Array.isArray(data) ? data : [];
      // Newest first — oldest test at the bottom.
      const sorted = [...list].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setTests(sorted);
      // Pre-select every test that hasn't been imported yet.
      setSelectedIds(
        new Set(sorted.filter((t) => t.exists !== 1).map((t) => t.id))
      );
      // Always enter step 2 with every accordion collapsed.
      setExpandedIds(new Set());
      setStep(2);
      // Prefetch all tests' biomarkers (batched) so counts/EQUIP render up front.
      loadPreviews(sorted);
    } catch (error) {
      console.error("Error linking InBody account:", error);
      notify(
        "Error",
        "Couldn't link the InBody account. Please check the account and try again.",
        "alert"
      );
    } finally {
      setIsLinking(false);
    }
  };

  const handleImport = async () => {
    const toImport = importableTests.filter((t) => selectedIds.has(t.id));
    if (toImport.length === 0 || isImporting) return;
    setIsImporting(true);
    try {
      // Reuse the prefetched biomarkers (GetData shape) and post them as-is to
      // the batch UpdateBiomarkers endpoint — one request per selected test, in
      // parallel. No refetch needed.
      await Promise.all(
        toImport.map((test) =>
          UpdateCostumerBiomarkers(customerId, previews[test.id]?.data ?? [])
        )
      );

      // Refresh the biomarker tab + platform data (same as the extractor save).
      const [, dataPlatform] = await Promise.all([
        ASGetClientBiomarkerTabDataHandler(customerId),
        getPlatformData(customerId, AuthUser.role),
      ]);
      dispatch(platformActions.updateDataPlatformServer(dataPlatform));
      onClose();

      // Same post-save handling as the extractor: prompt for the score review
      // when the import left scores pending, plain toast otherwise.
      showSavedSnackbar({
        report: dataPlatform,
        reviewTitle: "InBody results imported",
        fallback: {
          title: "InBody data imported",
          message: "The selected InBody tests have been imported.",
        },
      });
    } catch (error) {
      console.error("Error importing InBody data:", error);
      notify("Error", "Failed to import InBody data.", "alert");
    } finally {
      setIsImporting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title(theme)}>Sync InBody Data</div>
          <div onClick={onClose} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>

        <StepProgressBar step={step} total={TOTAL_STEPS} />

        {step === 1 ? (
          /* Step 1 — link account */
          <div style={styles.body}>
            <div style={styles.stepHeading(theme)}>Link InBody account</div>
            <div style={styles.stepDescription(theme)}>
              This client is not linked to an InBody profile yet. Link their
              account to fetch and view their tests in the Biometrics tab.
            </div>
            <div style={styles.inputWrapper}>
              <TextInputField
                label="InBody account"
                value={inBodyAccount}
                name="inBodyAccount"
                handleChange={(e) => setInBodyAccount(e.target.value)}
              />
            </div>
          </div>
        ) : (
          /* Step 2 — available tests */
          <div style={styles.body}>
            <div style={{ flexShrink: 0 }}>
              <div style={styles.stepHeading(theme)}>Available test(s)</div>
              <div style={styles.stepDescription(theme)}>
                Select which InBody tests to import for this client. Expand a
                test to preview its biomarkers.
              </div>
            </div>

            <div style={styles.selectRow}>
              <div style={styles.selectedCount(theme)}>
                {selectedIds.size} to {importableTests.length} selected
              </div>
              {importableTests.length > 0 && (
                <div style={styles.linkAction(theme)} onClick={toggleSelectAll}>
                  {allSelected ? "Deselect all" : "Select all"}
                </div>
              )}
            </div>

            <div
              style={{
                ...styles.testList,
                // When a test is expanded it grows to fill and scrolls
                // internally, so the list itself must not scroll. With nothing
                // expanded, let the list scroll if the collapsed cards overflow.
                overflowY: anyExpanded ? "hidden" : "auto",
              }}
            >
              {isLinking ? (
                <Skeleton variant="rectangular" width="100%" height={80} />
              ) : tests.length === 0 ? (
                <div style={styles.stepDescription(theme)}>
                  No InBody tests found for this client.
                </div>
              ) : (
                tests.map((test) => (
                  <InBodyTestCard
                    key={test.id}
                    test={test}
                    synced={test.exists === 1}
                    selected={selectedIds.has(test.id)}
                    onToggle={() => toggleSelect(test.id)}
                    expanded={expandedIds.has(test.id)}
                    onToggleExpand={() => toggleExpand(test.id)}
                    errored={erroredIds.has(test.id)}
                    biomarkers={
                      previews[test.id]?.status === "done"
                        ? previews[test.id].data
                        : null
                    }
                    loading={
                      !previews[test.id] ||
                      previews[test.id].status === "loading"
                    }
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          {step === 1 ? (
            <PrimaryButton
              label={isLinking ? "Linking…" : "Link & Continue"}
              onClick={handleLinkAndContinue}
              isDisable={!inBodyAccount.trim() || isLinking}
              icon={
                isLinking ? (
                  <CircularProgress size={16} sx={{ color: spinnerColor }} />
                ) : undefined
              }
              sx={{ justifyContent: "center", width: "auto" }}
              customLabelStyles={{ flex: "none" }}
            />
          ) : (
            <>
              <SecondaryButton
                label="Back"
                onClick={() => setStep(1)}
                isDisable={isImporting}
              />
              <PrimaryButton
                label={isImporting ? "Importing…" : "Import"}
                onClick={handleImport}
                isDisable={
                  selectedIds.size === 0 || isImporting || isPrefetching
                }
                icon={
                  isImporting ? (
                    <CircularProgress size={16} sx={{ color: spinnerColor }} />
                  ) : undefined
                }
                sx={{ justifyContent: "center", width: "auto" }}
                customLabelStyles={{ flex: "none" }}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InBodySyncModal;

const styles = {
  modalContainer: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "16px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "854px",
    height: "90vh",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    cursor: "pointer",
    padding: "8px",
    display: "flex",
  },
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flex: 1,
    minHeight: 0,
    // Inner areas (the test list / expanded cards) own the scrolling.
    overflow: "hidden" as "hidden",
  },
  stepHeading: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colorTokens.N[90],
  }),
  stepDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    letterSpacing: "-0.24px",
    color: theme.colorTokens.N[80],
  }),
  inputWrapper: {
    width: "100%",
  },
  selectRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0 as 0,
  },
  selectedCount: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colorTokens.N[80],
  }),
  linkAction: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
  }),
  testList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    flex: 1,
    minHeight: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
};
